import React, { useEffect, useState, useCallback } from "react";
import { Card, Row, Col, Spinner, Badge, Table } from "react-bootstrap";
import { useNavigate, useOutletContext, useLocation } from "react-router-dom";
import Header from "../components/Header";
import { getHost } from "../utils/utils";
import { FiAlertTriangle, FiActivity } from "react-icons/fi";

const API_ALERTS = `${getHost()}/api/metrics/alerts`;
const API_OBS_ALERTS = `${getHost()}/api/admin/observability/alerts`;
const getToken = () => localStorage.getItem("jwt");
const REFRESH_INTERVAL_MS = 30000;

function formatPercent(rate) {
  return `${(rate * 100).toFixed(1)}%`;
}

function getCbBadgeStyle(state) {
  if (state === "closed") return { backgroundColor: "#00E98E22", color: "#00E98E" };
  if (state === "half_open") return { backgroundColor: "#FFC10722", color: "#FFC107" };
  return { backgroundColor: "#FF444422", color: "#FF4444" };
}

function ruleBadgeStyle(ruleId) {
  switch (ruleId) {
    case "url_drift":
      return { backgroundColor: "#FF9F1C22", color: "#FF9F1C" };
    case "restock_missed":
      return { backgroundColor: "#FFC10722", color: "#FFC107" };
    case "cat_zero":
    case "search_zero":
    default:
      return { backgroundColor: "#FF444422", color: "#FF4444" };
  }
}

const Alerts = () => {
  const { toggleSidebar } = useOutletContext();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = location.pathname.startsWith("/admin");

  const [alerts, setAlerts] = useState([]);
  const [silentFailures, setSilentFailures] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAlerts = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${getToken()}` };
      // Both endpoints are admin-only on the backend; non-admin users get 403
      // for the silent-failures one (caught silently, table just stays empty).
      const [errorRes, silentRes] = await Promise.all([
        fetch(API_ALERTS, { headers }),
        fetch(API_OBS_ALERTS, { headers }).catch(() => null),
      ]);
      if (errorRes.ok) {
        const data = await errorRes.json();
        const sorted = (data.alerts || []).sort((a, b) => b.error_rate - a.error_rate);
        setAlerts(sorted);
      }
      if (silentRes?.ok) {
        const data = await silentRes.json();
        setSilentFailures(data.alerts || []);
      }
    } catch {
      // Keep existing state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  return (
    <div className="w-100 monitor-setting">
      <Header toggleSidebar={toggleSidebar} />
      <div className="pb-4 mx-4 content-body">
        <Row className="gx-0 align-items-center">
          <Col md={12}>
            <div className="mb-2">
              <div className="d-inline-flex align-items-center justify-content-center bg-dark rounded p-2 mb-2">
                <FiAlertTriangle className="text-white" />
              </div>
              <h4 className="fs-22 text-main mb-1">Alerts</h4>
              <p className="text-white-65 fs-14 mb-0">
                Error-rate alerts and silent-failure alerts surfaced from
                observability rollups
              </p>
            </div>
          </Col>
        </Row>
      </div>

      {/* Silent failures (observability) */}
      {isAdmin && (
        <div className="px-4 mb-4">
          <div className="d-flex align-items-center mb-2">
            <FiActivity className="me-2 text-white-65" />
            <h6 className="text-main fs-15 mb-0">Silent failures</h6>
            <Badge bg="" className="ms-2" style={{ backgroundColor: "#FF444422", color: "#FF4444" }}>
              {silentFailures.length}
            </Badge>
          </div>
          {loading ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" />
            </div>
          ) : silentFailures.length === 0 ? (
            <Card className="border border-dark bg-transparent">
              <Card.Body className="p-3 text-center">
                <p className="text-white-65 fs-13 mb-0">
                  No open silent-failure alerts. cat_zero / search_zero / url_drift / restock_missed all clear.
                </p>
              </Card.Body>
            </Card>
          ) : (
            <Card className="border border-dark bg-transparent">
              <Card.Body className="p-3">
                <div className="table-mobile-scroll">
                  <Table className="table custom-table table-bordered" hover>
                    <thead>
                      <tr>
                        <th className="fs-13 fw-500 border-bottom" style={{ width: "22%" }}>Monitor</th>
                        <th className="fs-13 fw-500 border-bottom" style={{ width: "15%" }}>Rule</th>
                        <th className="fs-13 fw-500 border-bottom" style={{ width: "33%" }}>Target</th>
                        <th className="fs-13 fw-500 border-bottom" style={{ width: "18%" }}>Opened</th>
                        <th className="fs-13 fw-500 border-bottom" style={{ width: "12%" }}>Last seen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {silentFailures.map((a) => (
                        <tr key={a._id}>
                          <td className="py-2">
                            <span
                              className="text-main fs-13 fw-500 cursor-pointer"
                              style={{ textDecoration: "underline", textDecorationColor: "#7C98FF44" }}
                              onClick={() =>
                                navigate(`/admin/observability/${encodeURIComponent(a.monitor)}`)
                              }
                            >
                              {a.monitor}
                            </span>
                          </td>
                          <td className="py-2">
                            <Badge bg="" className="fs-11 fw-500" style={ruleBadgeStyle(a.rule_id)}>
                              {a.rule_id}
                            </Badge>
                          </td>
                          <td className="text-white-65 fs-12 py-2">
                            <code className="text-main fs-12" style={{ wordBreak: "break-all" }}>
                              {a.bucket_key || "(monitor-level)"}
                            </code>
                          </td>
                          <td className="text-white-65 fs-12 py-2">
                            {a.opened_at ? new Date(a.opened_at).toLocaleString() : "—"}
                          </td>
                          <td className="text-white-65 fs-12 py-2">
                            {a.last_seen ? new Date(a.last_seen).toLocaleTimeString() : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          )}
        </div>
      )}

      {/* Error-rate alerts (existing) */}
      <div className="px-4 mb-4">
        <div className="d-flex align-items-center mb-2">
          <FiAlertTriangle className="me-2 text-white-65" />
          <h6 className="text-main fs-15 mb-0">Error-rate alerts</h6>
          <Badge bg="" className="ms-2" style={{ backgroundColor: "#FF444422", color: "#FF4444" }}>
            {alerts.length}
          </Badge>
        </div>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : alerts.length === 0 ? (
          <Card className="border border-dark bg-transparent">
            <Card.Body className="p-5 text-center">
              <p className="text-white-65 fs-14 mb-0">No active alerts. All monitors are operating normally.</p>
            </Card.Body>
          </Card>
        ) : (
          <Card className="border border-dark bg-transparent">
            <Card.Body className="p-3">
              <div className="table-mobile-scroll">
              <Table className="table custom-table table-bordered" hover>
                <thead>
                  <tr>
                    <th className="fs-13 fw-500 border-bottom" style={{ width: "25%" }}>Monitor</th>
                    <th className="fs-13 fw-500 border-bottom" style={{ width: "10%" }}>Error Rate</th>
                    <th className="fs-13 fw-500 border-bottom" style={{ width: "12%" }}>Circuit Breaker</th>
                    <th className="fs-13 fw-500 border-bottom" style={{ width: "35%" }}>Top Error</th>
                    <th className="fs-13 fw-500 border-bottom" style={{ width: "18%" }}>Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map((alert) => (
                    <tr key={alert.monitor}>
                      <td className="py-2">
                        <span
                          className="text-main fs-13 fw-500 cursor-pointer"
                          style={{ textDecoration: "underline", textDecorationColor: "#7C98FF44" }}
                          onClick={() =>
                            navigate(`/monitors/health/${encodeURIComponent(alert.monitor)}`)
                          }
                        >
                          {alert.monitor}
                        </span>
                      </td>
                      <td className="py-2">
                        <span className="fs-12 fw-600" style={{ color: "#FF4444" }}>
                          {formatPercent(alert.error_rate)}
                        </span>
                      </td>
                      <td className="py-2">
                        {alert.cb_state && (
                          <Badge bg="" className="fs-11 fw-500" style={getCbBadgeStyle(alert.cb_state)}>
                            {alert.cb_state}
                          </Badge>
                        )}
                      </td>
                      <td className="text-white-65 fs-12 py-2">
                        {alert.errors && alert.errors.length > 0
                          ? alert.errors[0].pattern
                          : "N/A"}
                      </td>
                      <td className="text-white-65 fs-12 py-2">
                        {alert.ts ? new Date(alert.ts).toLocaleString() : "N/A"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              </div>
            </Card.Body>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Alerts;
